import { useRef, useState } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { RUBRICS } from "@/data/content";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  },
};

export default function Rubriche({ onSelect }) {
  const sectionRef = useRef(null);
  const [hovered, setHovered] = useState(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 140, damping: 20 });
  const sy = useSpring(my, { stiffness: 140, damping: 20 });

  const onMove = (e) => {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set(e.clientX - rect.left);
    my.set(e.clientY - rect.top);
  };

  return (
    <section
      id="rubriche"
      ref={sectionRef}
      onMouseMove={onMove}
      data-testid="rubriche-section"
      className="relative bg-ink px-5 md:px-10 py-20 md:py-32"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
        <div>
          <motion.p
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="font-mono text-[10px] md:text-xs uppercase tracking-[0.35em] text-ember mb-4"
          >
            Le rubriche
          </motion.p>
          <motion.h2
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="font-serif font-light text-3xl md:text-5xl tracking-tight"
          >
            Cinque modi di guardare <span className="italic text-ember">l'acqua</span>
          </motion.h2>
        </div>
        <motion.p
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="max-w-sm text-sm text-mist leading-relaxed"
        >
          Ogni rubrica è un punto di vista. Scegli un filo e segui lo stagno:
          i numeri si aprono sotto.
        </motion.p>
      </div>

      <div className="relative border-t border-ink-border">
        <AnimatePresence>
          {hovered !== null && (
            <motion.div
              key="preview"
              initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
              animate={{ opacity: 1, scale: 1, rotate: 2 }}
              exit={{ opacity: 0, scale: 0.85, rotate: 4 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              style={{ x: sx, y: sy }}
              data-testid="rubric-preview"
              className="pointer-events-none absolute left-0 top-0 z-20 hidden lg:block w-[300px] aspect-[3/4] -translate-x-1/2 -translate-y-1/2 overflow-hidden border border-ink-border"
            >
              <img
                src={RUBRICS[hovered].image}
                alt=""
                className="h-full w-full object-cover"
              />
            </motion.div>
          )}
        </AnimatePresence>

        {RUBRICS.map((r, i) => (
          <motion.button
            key={r.id}
            data-testid={`rubric-row-${r.id}`}
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onSelect(r.id)}
            className="group relative grid grid-cols-[auto_1fr_auto] md:grid-cols-[70px_1fr_1fr_auto] items-center gap-4 md:gap-8 w-full text-left border-b border-ink-border py-7 md:py-9 px-1 md:px-3 transition-colors duration-500 hover:bg-ink-surface"
          >
            <span className="font-mono text-xs md:text-sm text-faint group-hover:text-ember transition-colors duration-300">
              {r.num}
            </span>
            <span className="font-serif font-light text-2xl md:text-4xl lg:text-5xl tracking-tight transition-transform duration-500 group-hover:translate-x-2 group-hover:text-bone">
              {r.title}
            </span>
            <span className="hidden md:block font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-faint max-w-xs">
              {r.en}
              <span className="block mt-2 font-serif normal-case tracking-normal text-sm text-mist leading-relaxed">
                {r.desc}
              </span>
            </span>
            <ArrowUpRight
              size={26}
              strokeWidth={1.2}
              className="text-faint transition-all duration-500 group-hover:text-ember group-hover:rotate-45"
            />
          </motion.button>
        ))}
      </div>
    </section>
  );
}
